import {
  IconButton,
  InputAdornment,
  TextField as MuiTextField,
  Skeleton,
} from "@mui/material";
import React, { Component } from "react";
import Theme from "../common/Theme";
import RemoveIcon from "../resources/icons-remove.svg";

export default class TextField extends Component<any> {
  static defaultProps = {
    type: null,
    detectAutoFill: false,
    mimicField: null,
  };

  ref: any;
  inputRef: any;
  inputRef2: any;
  autofilled: boolean;

  constructor(props: any) {
    super(props);

    this.ref = React.createRef();

    this.state = {
      valid: null,
      focus: false,
    };

    this.inputRef2 = React.createRef();
    this.autofilled = false;

    if (props.mimicField) {
      const field = document.getElementById(props.mimicField);

      if (field) {
        if (this.props.detectAutoFill) {
          field.addEventListener("onautocomplete", (e) => {
            this.onAutoFillStart(e);
          });
        }
      }
    }
  }

  componentDidUpdate(prevProps: any) {
    if (this.props.forceBlur !== prevProps.forceBlur && this.props.forceBlur)
      this.forceBlur();
  }

  componentDidMount() {
    const { detectAutoFill } = this.props;

    if (detectAutoFill && this.inputRef2 && this.inputRef2.current) {
      this.inputRef2.current.addEventListener("onautocomplete", (e: any) => {
        if (e.target.hasAttribute("autocompleted")) this.onAutoFillStart(e);
        else this.onAutoFillCancel(e);
      });
    }
  }

  onAutoFillStart(e: any) {
    this.autofilled = true;
    this.onChange(e);
  }

  onAutoFillCancel(e: any) {
    this.autofilled = false;
    this.onChange(e);
  }

  forceBlur() {
    if (this.inputRef) this.inputRef.blur();
  }

  getInputRef() {
    const { inputProps, inputRef } = this.props;
    return inputProps && inputProps.ref && inputProps.ref.current
      ? inputProps.ref.current.node
      : inputRef
      ? inputRef
      : this.ref.current;
  }

  clearValue() {
    const { defaultValue, onClear } = this.props;

    const inputRef = this.getInputRef();
    if (!inputRef) return;

    if (!this.props.select) {
      inputRef.value = "";

      var e = new Event("input", {
        bubbles: true,
        cancelable: true,
        target: inputRef,
      } as any);

      inputRef.dispatchEvent(e);
    } else if (this.props.onChange) {
      this.props.onChange({ target: { value: defaultValue } } as any);
    }

    inputRef.focus();

    if (onClear) onClear();
  }

  onInput(e: any) {
    const { onChange, validationMethod } = this.props;
    const { valid }: any = this.state;

    const inputRef = this.getInputRef();

    if (inputRef && inputRef.setAttribute)
      inputRef.setAttribute("size", e.target.value.length);

    if (onChange) onChange(e);

    if (validationMethod) {
      const newValid = validationMethod(e, e.target.value);
      if (newValid !== valid) this.setState({ valid: newValid });
    }
  }

  onChange(e: any) {
    const { onChange } = this.props;
    if (onChange) onChange(e);
  }

  onFocus(e: any) {
    const { onFocus } = this.props;
    this.setState({ focus: true });

    if (onFocus) onFocus(e);
  }

  onBlur(e: any) {
    const { onBlur, validationMethod } = this.props;
    const { valid }: any = this.state;

    if (validationMethod) {
      const newValid = validationMethod(e, e.target.value);
      if (newValid !== valid) this.setState({ valid: newValid, focus: false });
    } else this.setState({ focus: false });

    if (onBlur) onBlur(e);
  }

  onKeyDown(e: any) {
    const { onEnter } = this.props;
    if (onEnter && e.keyCode == 13) onEnter(e);
  }

  setInputRef(inputRef: any) {
    if (inputRef) this.inputRef = inputRef;
  }

  render() {
    const {
      id,
      ref,
      inputRef,
      children,
      label,
      value,
      readOnly,
      pattern,
      defaultValue,
      onClick,
      type,
      onEnter,
      disabled,
      autoComplete,
      autoFocus,
      rows,
      loading,
      className,
      sx,
      maxLength,
      placeholder,
      focusPlaceholder,
      dense,
      leadingIcon,
      multiline,
      helperText,
      error,
      endAdornment,
      InputProps,
      InputLabelProps,
      fullWidth,
      inputProps,
      select,
      SelectProps,
      minRows,
      maxRows,
      validationText,
      inputSx,
    }: any = this.props;
    const clearable =
      this.props.clearable !== false &&
      (select ? value !== defaultValue : value && value.length !== 0);
    const { valid, focus }: any = this.state;

    return loading ? (
      <div className="klayo-textfield--loading">
        <Skeleton width={70} height={20} />
        <Skeleton width={sx.width || null} height={42} />
      </div>
    ) : (
      <MuiTextField
        id={id}
        ref={ref}
        inputRef={this.inputRef2}
        className={
          "klayo-textfield " +
          (dense ? " klayo-textfield-dense " : "") +
          (label ? " klayo-textfield-withlabel " : "") +
          (className ? className : "") +
          (this.autofilled ? " klayo-textfield--autofilled" : "")
        }
        label={label}
        variant="outlined"
        select={select}
        SelectProps={SelectProps}
        placeholder={focusPlaceholder && focus ? focusPlaceholder : placeholder}
        multiline={multiline}
        rows={rows}
        minRows={minRows}
        maxRows={maxRows}
        onInput={this.onInput.bind(this)}
        onChange={this.onChange.bind(this)}
        onClick={onClick}
        onFocus={this.onFocus.bind(this)}
        onKeyDown={this.onKeyDown.bind(this)}
        onBlur={this.onBlur.bind(this)}
        disabled={disabled}
        helperText={valid === false ? validationText : helperText}
        fullWidth={fullWidth}
        error={error || valid === false}
        InputLabelProps={{
          shrink: true,
          variant: "filled",
          ...InputLabelProps,
        }}
        autoComplete={
          typeof autoComplete === "string"
            ? autoComplete
            : autoComplete === false
            ? "off"
            : "on"
        }
        autoFocus={autoFocus}
        value={value ? value : ""}
        inputProps={{
          ref: inputRef ? inputRef : this.ref,
          maxLength,
          pattern,
          ...inputProps,
        }}
        InputProps={{
          notched: false,
          readOnly: readOnly,
          type: type,
          startAdornment: leadingIcon ? (
            <InputAdornment position="start">{leadingIcon}</InputAdornment>
          ) : null,
          endAdornment: endAdornment ? (
            endAdornment
          ) : (
            <InputAdornment position="end">
              <IconButton
                className="klayo-textfield_clearbutton"
                sx={{ display: clearable ? "flex" : "none" }}
                onClick={this.clearValue.bind(this)}
              >
                <RemoveIcon />
              </IconButton>
            </InputAdornment>
          ),
          ...InputProps,
        }}
        sx={{
          ...sx,
          "& .MuiOutlinedInput-root": {
            "& fieldset": {
              borderColor: Theme.getStyleVar("--neon-blue-nb-200"),
            },
            "&:hover fieldset": {
              borderColor: Theme.getStyleVar("--neon-blue-nb-300"),
            },
            "&.Mui-focused fieldset": {
              borderWidth: "2px",
              borderColor: Theme.getStyleVar("--neon-blue-nb-500"),
            },
          },
          "& .MuiInputLabel-root.Mui-focused": {
            color: Theme.getStyleVar("--neon-blue-nb-500"),
          },
        }}
      >
        {children}
      </MuiTextField>
    );
  }
}
